/*
 * Copyright 2024, Corning Optical Communications
 * All Rights Reserved.
 */

import React, { useMemo } from "react";
import { CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from "@mui/material";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { selectedDeviceSelector } from "../../../store/api/devices/selectors";
import { useGetDasInventoryQuery } from "../../../store/api/devices/deviceApiSlice";
import { parseDeviceAttributes, isBitSet, getAlarmLedColor } from "../../../components/Drawers/DasDeviceDetails/utils";
import DetailRow from "../../../components/Drawers/DasDeviceDetails/components/DetailRow";
import AlarmTable from "../../../components/Drawers/DasDeviceDetails/components/AlarmTable";
import { AlarmDefinition } from "../../../components/Drawers/DasDeviceDetails/types";
import { resolveDeviceStatusLabel } from "../../../utils/deviceStatus";

const ControllerInfo: React.FC = () => {
  const location = useLocation();
  const selectedDevice = useSelector(selectedDeviceSelector);
  const deviceObjectKey = selectedDevice?.objectKey || location.pathname.split("/")[2];

  const { data: dasInventory = [], isLoading } = useGetDasInventoryQuery(
    { deviceKey: deviceObjectKey },
    {
      skip: !deviceObjectKey,
      pollingInterval: 30000,
    }
  );

  const controllerDevice = useMemo(() => {
    const candidates = dasInventory.filter((device) => {
      const type = (device.equipmentType || "").toUpperCase();
      return type.includes("A3") || type.includes("A2") || type.includes("AU2");
    });
    const root = candidates.find((device) => device.equipmentIndex === "0");
    return root || candidates[0];
  }, [dasInventory]);

  const parsedAttributes = useMemo(() => {
    if (!controllerDevice?.attributes) return {};
    return parseDeviceAttributes(controllerDevice.attributes);
  }, [controllerDevice]);

  const dcuDevice = useMemo(() => {
    const rootDcu = dasInventory.find((device) => {
      const type = (device.equipmentType || "").toUpperCase();
      return type === "DCU" && device.equipmentIndex === "0";
    });

    if (rootDcu) return rootDcu;

    return dasInventory.find((device) => {
      const type = (device.equipmentType || "").toUpperCase();
      return type.includes("DCU");
    });
  }, [dasInventory]);

  const parsedDcuAttributes = useMemo(() => {
    if (!dcuDevice?.attributes) return {};
    return parseDeviceAttributes(dcuDevice.attributes);
  }, [dcuDevice]);

  const statusLabel = controllerDevice ? resolveDeviceStatusLabel(controllerDevice.status) : "Unknown";
  const isDisconnected = statusLabel === "Disconnected";

  // Detect if device is MA6200-G1 by checking the parent Services Device model
  const isMA6200G1 = useMemo(() => {
    if (!selectedDevice) return false;
    const model = selectedDevice.modelName?.toUpperCase() || "";
    const detected = model.includes("MA6200-G1") || model.includes("MA6200G1");
    console.log("[ControllerInfo] Device detection - Model:", selectedDevice.modelName, "isMA6200G1:", detected);
    return detected;
  }, [selectedDevice]);

  // Detect if device is MA6200-G2
  const isMA6200G2 = useMemo(() => {
    const model = selectedDevice?.modelName?.toUpperCase() || "";
    if ( model.includes("MA6200-G2") || model.includes("MA6200G2") 
     ) {
      return true;
    }

    if (!selectedDevice) return Boolean(dcuDevice);

    // Fallback: if the controller device has A3 equipment type it's G2
    const ctrlType = (controllerDevice?.equipmentType || "").toUpperCase();
    return ctrlType.includes("A3") || Boolean(dcuDevice);
  }, [selectedDevice, controllerDevice, dcuDevice]);

  const alarmDefinitions: AlarmDefinition[] = useMemo(
    () => [
      { bit: 0, name: "HW Failure" },
      { bit: 1, name: "Adjustment Fault" },
      { bit: 2, name: "Installation Fault" },
      { bit: 3, name: "SW Release Mismatch" },
      { bit: 4, name: "Connectivity" },
    ],
    []
  );

  const hasActiveAlarms = useMemo(() => {
    if (parsedAttributes.alarms === undefined) return false;
    return alarmDefinitions.some((alarm) => isBitSet(parsedAttributes.alarms, alarm.bit));
  }, [parsedAttributes.alarms, alarmDefinitions]);

  const statusLedColor = getAlarmLedColor(isDisconnected, hasActiveAlarms);

  const formatAgcMode = (value: any) => {
    if (value === undefined || value === null) return "N/A";
    const val = String(value);
    switch (val) {
      case "0":
        return "Off";
      case "1":
        return "On";
      default:
        return val;
    }
  };

  const formatDhcpMode = (value: any) => {
    if (value === undefined || value === null) return "N/A";
    const val = String(value);
    switch (val) {
      case "0":
        return "Static IP";
      case "1":
        return "DHCP";
      case "2":
        return "Auto IP";
      case "3":
        return "DHCP with fallback";
      case "4":
        return "Stateless DHCPv6";
      case "5":
        return "Stateful DHCPv6";
      default:
        return val;
    }
  };

  const formatTddConfig = (value: any) => {
    if (value === undefined || value === null) return "N/A";
    const val = String(value);
    switch (val) {
      case "0":
        return "Configuration 0";
      case "1":
        return "Configuration 1";
      case "2":
        return "Configuration 2";
      case "3":
        return "Configuration 3";
      case "4":
        return "Configuration 4";
      case "5":
        return "Configuration 5";
      case "6":
        return "Configuration 6";
      default:
        return val;
    }
  };

  const formatTddCpType = (value: any) => {
    if (value === undefined || value === null) return "N/A";
    const val = String(value);
    switch (val) {
      case "0":
        return "Normal";
      case "1":
        return "Extended";
      default:
        return val;
    }
  };

  const formatTddCenterFreq = (value: any) => {
    if (value === undefined || value === null) return "N/A";
    const val = String(value);
    switch (val) {
      case "2526":
        return "2526 MHz (Low band)";
      case "2593":
        return "2593 MHz (Medium band)";
      case "2660":
        return "2660 MHz (High band)";
      default:
        return val ? `${val} MHz` : "N/A";
    }
  };

  // Format TDD switch/status values (1 = On, 0 = Off)
  const formatOnOff = (value: any): string => {
    if (value === undefined || value === null) return "N/A";
    const val = String(value);
    return val === "1" ? "On" : val === "0" ? "Off" : val;
  };

  const displayValue = (value: any): string => {
    if (value === undefined || value === null || value === "") return "N/A";
    return String(value);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <CircularProgress size="4rem" color="primary" />
      </div>
    );
  }

  if (!controllerDevice && !(isMA6200G2 && dcuDevice)) {
    return (
      <div className="p-4">
        <p className="text-sm text-muted-foreground">No controller device found in this DAS system.</p>
      </div>
    );
  }

  // MA6200-G2 Layout
  if (isMA6200G2) {
    const g2Attributes = controllerDevice ? parsedAttributes : parsedDcuAttributes;
    const lanConnectivity = g2Attributes.lanConnectivity || {};
    const bandConfiguration = g2Attributes.bandConfiguration || {};  
    const hasTddData = Object.keys(bandConfiguration).length > 0;

 return (
      <div className="space-y-4">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">IP Configuration</CardTitle></CardHeader>
          <CardContent>
            <DetailRow label="Primary IP" value={lanConnectivity.primaryNMSIpAddress || "N/A"} />
            <DetailRow label="Local IP" value={lanConnectivity.deviceIpAddr || "N/A"} />
            <DetailRow label="Subnet Mask" value={lanConnectivity.subnetMask || "N/A"} />
            <DetailRow label="Default Gateway" value={lanConnectivity.defaultGateway || "N/A"} />
            <DetailRow label="SNMP Listening Port" value={lanConnectivity.primaryNMSPortNumber || "N/A"} />
            <DetailRow label="UDP Receive Port" value={lanConnectivity.deviceRecvPortForUDP || "N/A"} />
            <DetailRow label="Heartbeat" value={lanConnectivity.heartbeatClock|| "N/A"} />
            <DetailRow label="NTP Primary Server" value={lanConnectivity.ntpPrimaryServerIpAddress || "N/A"} />
            <DetailRow label="NTP Secondary Server" value={lanConnectivity.ntpSecondaryServerIpAddress || "N/A"} />
          </CardContent>
        </Card>

        {/* TDD Configuration Section - Primary A3 Only */}
        {hasTddData && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">TDD Configuration</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {/* 5G */}
              <div className="border-l-4 border-green-500 bg-green-500/10 px-3 py-2 rounded">
                <span className="text-sm font-semibold text-green-600 dark:text-green-400">5G Configuration</span>
              </div>
              <div className="grid grid-cols-2 gap-x-4">
                <DetailRow label="TDD Switch 5G"              value={formatOnOff(bandConfiguration.tddSwitch5G)} />
                <DetailRow label="Sync Status 5G"             value={formatOnOff(bandConfiguration.syncStatus5G)} />
                <DetailRow label="SSB Type"                   value={bandConfiguration.ssbType || "N/A"} />
                <DetailRow label="Slot Configuration 5G"      value={bandConfiguration.slotConfiguration5G|| "N/A"} />
                <DetailRow label="Special Slot Symbol Format 1" value={bandConfiguration.specialSlotsymbolFormat1 || "N/A"} />
                <DetailRow label="Special Slot Symbol Format 2" value={bandConfiguration.specialSlotsymbolFormat2 || "N/A"} />
                <DetailRow label="SSB ARFCN 5G"               value={bandConfiguration.ssbARFCN5G || "N/A"} />
              </div>
              <p className="text-xs font-semibold text-foreground">Radio Modules (5G)</p>
              <TableContainer component={Paper} sx={{ backgroundColor: "var(--color-card)", border: "1px solid var(--color-border)" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {[1,2,3,4].map(m => <TableCell key={m} sx={{ fontWeight: 600, backgroundColor: "rgba(76,175,80,0.1)", color: "var(--color-foreground)" }}>Module {m}</TableCell>)}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      {[1,2,3,4].map(m => <TableCell key={m} sx={{ color: "var(--color-foreground)" }}>{formatOnOff((bandConfiguration as any)[`radio5GModule${m}`])}</TableCell>)}
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {/* 4G */}
              <div className="border-l-4 border-blue-500 bg-blue-500/10 px-3 py-2 rounded mt-2">
                <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">4G Configuration</span>
              </div>
              <div className="grid grid-cols-2 gap-x-4">
                <DetailRow label="TDD Switch 4G"               value={formatOnOff(bandConfiguration.tddSwitch4G)} />
                <DetailRow label="Sync Status 4G"              value={formatOnOff(bandConfiguration.syncStatus4G)} />
                <DetailRow label="UL/DL Configuration"         value={bandConfiguration.uldlConfiguration || "N/A"} />
                <DetailRow label="Special Subframe Config"     value={bandConfiguration.specialSubframeConfiguration || "N/A"} />
              </div>
              <p className="text-xs font-semibold text-foreground">Radio Modules (4G)</p>
              <TableContainer component={Paper} sx={{ backgroundColor: "var(--color-card)", border: "1px solid var(--color-border)" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {[1,2,3,4].map(m => <TableCell key={m} sx={{ fontWeight: 600, backgroundColor: "rgba(33,150,243,0.1)", color: "var(--color-foreground)" }}>Module {m}</TableCell>)}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      {[1,2,3,4].map(m => <TableCell key={m} sx={{ color: "var(--color-foreground)" }}>{formatOnOff((bandConfiguration as any)[`radio4GModule${m}`])}</TableCell>)}
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // MA6200-G1 Layout
  if (isMA6200G1) {
    const lanConnectivity = parsedAttributes.lanConnectivity || {};
    const bandConfiguration = parsedAttributes.bandConfiguration || {};
    const hasTddData = Object.keys(bandConfiguration).length > 0;
    
    return (
      <div className="space-y-4">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">IP Configuration</CardTitle></CardHeader>
          <CardContent>
            <DetailRow label="Primary IP" value={lanConnectivity.primaryNMSIpAddress || "N/A"} />
            <DetailRow label="Local IP" value={lanConnectivity.deviceIpAddr || "N/A"} />
            <DetailRow label="Subnet Mask" value={lanConnectivity.subnetMask || "N/A"} />
            <DetailRow label="Default Gateway" value={lanConnectivity.defaultGateway || "N/A"} />
            <DetailRow label="SNMP Listening Port" value={lanConnectivity.primaryNMSPortNumber || "N/A"} />
            <DetailRow label="UDP Receive Port" value={lanConnectivity.deviceRecvPortForUDP || "N/A"} />
            <DetailRow label="Heartbeat" value={lanConnectivity.heartbeatClock|| "N/A"} />
            <DetailRow label="NTP Primary Server" value={lanConnectivity.ntpPrimaryServerIpAddress || "N/A"} />
            <DetailRow label="NTP Secondary Server" value={lanConnectivity.ntpSecondaryServerIpAddress || "N/A"} />
          </CardContent>
        </Card>

        {/* TDD Configuration Section - Primary A2 Only */}
        {hasTddData && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">TDD Configuration</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {/* 5G */}
              <div className="border-l-4 border-green-500 bg-green-500/10 px-3 py-2 rounded">
                <span className="text-sm font-semibold text-green-600 dark:text-green-400">5G Configuration</span>
              </div>
              <div className="grid grid-cols-2 gap-x-4">
                <DetailRow label="TDD Switch 5G"              value={formatOnOff(bandConfiguration.tddSwitch5G)} />
                <DetailRow label="Sync Status 5G"             value={formatOnOff(bandConfiguration.syncStatus5G)} />
                <DetailRow label="SSB Type"                   value={bandConfiguration.ssbType || "N/A"} />
                <DetailRow label="Slot Configuration 5G"      value={bandConfiguration.slotConfiguration5G|| "N/A"} />
                <DetailRow label="Special Slot Symbol Format 1" value={bandConfiguration.specialSlotsymbolFormat1 || "N/A"} />
                <DetailRow label="Special Slot Symbol Format 2" value={bandConfiguration.specialSlotsymbolFormat2 || "N/A"} />
                <DetailRow label="SSB ARFCN 5G"               value={bandConfiguration.ssbARFCN5G || "N/A"} />
              </div>
              <p className="text-xs font-semibold text-foreground">Radio Modules (5G)</p>
              <TableContainer component={Paper} sx={{ backgroundColor: "var(--color-card)", border: "1px solid var(--color-border)" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {[1,2,3,4].map(m => <TableCell key={m} sx={{ fontWeight: 600, backgroundColor: "rgba(76,175,80,0.1)", color: "var(--color-foreground)" }}>Module {m}</TableCell>)}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      {[1,2,3,4].map(m => <TableCell key={m} sx={{ color: "var(--color-foreground)" }}>{formatOnOff((bandConfiguration as any)[`radio5GModule${m}`])}</TableCell>)}
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {/* 4G */}
              <div className="border-l-4 border-blue-500 bg-blue-500/10 px-3 py-2 rounded mt-2">
                <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">4G Configuration</span>
              </div>
              <div className="grid grid-cols-2 gap-x-4">
                <DetailRow label="TDD Switch 4G"               value={formatOnOff(bandConfiguration.tddSwitch4G)} />
                <DetailRow label="Sync Status 4G"              value={formatOnOff(bandConfiguration.syncStatus4G)} />
                <DetailRow label="UL/DL Configuration"         value={bandConfiguration.uldlConfiguration || "N/A"} />
                <DetailRow label="Special Subframe Config"     value={bandConfiguration.specialSubframeConfiguration || "N/A"} />
              </div>
              <p className="text-xs font-semibold text-foreground">Radio Modules (4G)</p>
              <TableContainer component={Paper} sx={{ backgroundColor: "var(--color-card)", border: "1px solid var(--color-border)" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {[1,2,3,4].map(m => <TableCell key={m} sx={{ fontWeight: 600, backgroundColor: "rgba(33,150,243,0.1)", color: "var(--color-foreground)" }}>Module {m}</TableCell>)}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      {[1,2,3,4].map(m => <TableCell key={m} sx={{ color: "var(--color-foreground)" }}>{formatOnOff((bandConfiguration as any)[`radio4GModule${m}`])}</TableCell>)}
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // MA3000 Layout (default)
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Local IP Settings</CardTitle></CardHeader>
        <CardContent>
          <DetailRow label="Local DHCP Mode"  value={formatDhcpMode(parsedAttributes.getLocalDHCP)} />
          <DetailRow label="Local IP Address" value={parsedAttributes.getLocalIPAddress} />
          <DetailRow label="Local Subnet Mask" value={parsedAttributes.getLocalSubMask} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">LAN IP Settings</CardTitle></CardHeader>
        <CardContent>
          <DetailRow label="IP Address"           value={controllerDevice.ipAddress} />
          <DetailRow label="MAC Address"          value={controllerDevice.macAddress} />
          <DetailRow label="LAN DHCP Mode"        value={formatDhcpMode(parsedAttributes.getLanDHCP)} />
          <DetailRow label="LAN IP Address"       value={parsedAttributes.getLanIPAddress} />
          <DetailRow label="LAN Subnet Mask"      value={parsedAttributes.getLanSubMask} />
          <DetailRow label="LAN Default Gateway"  value={parsedAttributes.getLanDefaultGateway} />
          <DetailRow label="LAN IPv6 DHCP Mode"   value={formatDhcpMode(parsedAttributes.getLanV6DHCP)} />
          <DetailRow label="LAN IPv6 Address"     value={parsedAttributes.getLanIPv6Address} />
          <DetailRow label="LAN IPv6 Subnet Mask" value={parsedAttributes.getLanV6SubMask} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Settings</CardTitle></CardHeader>
        <CardContent>
          <DetailRow label="AGC Mode" value={formatAgcMode(parsedAttributes.agcSwitch)} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">TDD Configuration</CardTitle></CardHeader>
        <CardContent>
          <DetailRow label="TDD Allocation Mode"      value={formatTddConfig(parsedAttributes.hcmTDDAllocationMode || parsedAttributes.tddAllocationMode || parsedAttributes.tddConfig)} />
          <DetailRow label="TDD CP Type"              value={formatTddCpType(parsedAttributes.hcmTDDCpType || parsedAttributes.tddCpType)} />
          <DetailRow label="TDD Sub Frame Allocation" value={parsedAttributes.hcmTDDSubFrameAllocation || parsedAttributes.tddSubFrameAllocation || "N/A"} />
          <DetailRow label="TDD Center Frequency"     value={formatTddCenterFreq(parsedAttributes.hcmTDDCenterFreq || parsedAttributes.tddCenterFreq)} />
        </CardContent>
      </Card>

      {(parsedAttributes.alarms !== undefined || parsedAttributes.alarmsMask !== undefined) && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Alarms</CardTitle></CardHeader>
          <CardContent>
            <div className="flex items-center gap-2 mb-4">
              <span className="text-sm font-semibold text-foreground">Overall Status:</span>
              <span className="inline-block w-4 h-4 rounded-full border border-black/20 shrink-0"
                style={{ backgroundColor: statusLedColor }} />
              <span className="text-sm text-muted-foreground">
                {isDisconnected ? "Disconnected" : hasActiveAlarms ? "Alarms Present" : "Normal"}
              </span>
            </div>
            <AlarmTable
              alarms={parsedAttributes.alarms}
              alarmsMask={parsedAttributes.alarmsMask}
              alarmDefinitions={alarmDefinitions}
              isDisconnected={isDisconnected}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ControllerInfo;
